import { normalizeValueForPurpose } from '@/shared/camera/value-scan-normalizers';
import type { ValueScanPurpose } from '@/shared/camera/value-scan-purposes';

export { VALUE_SCAN_PURPOSES, type ValueScanPurpose } from '@/shared/camera/value-scan-purposes';

export const VALUE_SCAN_CANDIDATE_KINDS = {
  barcode: 'barcode',
  ocr: 'ocr',
} as const;

export type ValueScanCandidateKind = (typeof VALUE_SCAN_CANDIDATE_KINDS)[keyof typeof VALUE_SCAN_CANDIDATE_KINDS];

export interface ValueScanCandidate {
  confidence: number;
  format?: string;
  id: string;
  kind: ValueScanCandidateKind;
  purpose: ValueScanPurpose;
  rawValue: string;
  value: string;
}

interface CreateValueScanCandidateInput {
  confidence?: number;
  format?: string;
  kind: ValueScanCandidateKind;
  purpose: ValueScanPurpose;
  rawValue: string;
}

export function createValueScanCandidate(input: CreateValueScanCandidateInput): ValueScanCandidate | null {
  const value = normalizeValueForPurpose(input.rawValue, input.purpose);

  if (!value) {
    return null;
  }

  return {
    confidence: normalizeConfidence(input.confidence ?? getDefaultConfidence(input.kind)),
    format: input.format,
    id: `${input.kind}:${input.purpose}:${value}`,
    kind: input.kind,
    purpose: input.purpose,
    rawValue: input.rawValue,
    value,
  };
}

export function sortValueScanCandidates(candidates: ValueScanCandidate[]) {
  return candidates.toSorted(
    (left, right) => right.confidence - left.confidence || left.value.localeCompare(right.value),
  );
}

function getDefaultConfidence(kind: ValueScanCandidateKind) {
  return kind === VALUE_SCAN_CANDIDATE_KINDS.barcode ? 0.98 : 0.7;
}

function normalizeConfidence(confidence: number) {
  return Math.min(1, Math.max(0, confidence));
}
